/*
 * Stratus — homepage Film (st-home-film.js), loaded with defer by sections/st-home-film.liquid. No library.
 *
 * <st-home-film> attaches its film (the <template class="st-home-film__source">) when it comes within a screen of
 * the viewport, plays it muted while at least a quarter of it is in view, and pauses it out of view. Under
 * prefers-reduced-motion it never starts on its own and shows Play. A visitor's Pause sticks: scrolling back does
 * not restart the film. The film is always muted (no sound button). Play/Pause swaps its glyph (CSS, from
 * data-playing on the root) and its aria-label (the stratus.home.film.* strings passed as data-label-* attributes).
 * The button follows what the film is doing, not what was asked: it shows Pause only once the video fires `playing`,
 * and Play again on `pause`. So a film the phone holds back (iPhone, WIL-701) shows Play, and a tap starts it.
 * Phones play the Shopify video's streaming or 720p rendition (video_tag sources), not the 2560 × 1440 original; and
 * a play() that iOS leaves pending while it buffers is asked again on `canplay`. iOS Low Power Mode still refuses any
 * autoplay on every site: the poster and Play show then.
 */
(() => {
  if (customElements.get('st-home-film')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class StHomeFilm extends HTMLElement {
    connectedCallback() {
      this.template = this.querySelector('template.st-home-film__source');
      this.playButton = this.querySelector('[data-film-play]');
      if (!this.template) return;

      this.video = null;
      this.inView = false;
      this.isPlaying = false;
      // The visitor's choice wins over autoplay once they have pressed Play or Pause.
      this.wantsPlay = !reducedMotion.matches;

      this.onPlayClick = () => this.togglePlay();
      this.playButton?.addEventListener('click', this.onPlayClick);

      this.render();

      if ('IntersectionObserver' in window) {
        this.nearObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.attach();
              this.nearObserver.disconnect();
            }
          },
          { rootMargin: '100% 0px' }
        );
        this.nearObserver.observe(this);

        this.viewObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.inView = entry.isIntersecting;
            this.sync();
          },
          { threshold: 0.25 }
        );
        this.viewObserver.observe(this);
      } else {
        this.inView = true;
        this.attach();
        this.sync();
      }
    }

    disconnectedCallback() {
      this.nearObserver?.disconnect();
      this.viewObserver?.disconnect();
      this.playButton?.removeEventListener('click', this.onPlayClick);
      this.video?.pause();
    }

    attach() {
      if (this.video || !this.template) return this.video;
      const fragment = this.template.content.cloneNode(true);
      const video = fragment.querySelector('video');
      if (!video) return null;
      // The poster is already on the page; a second one from video_tag would download for nothing.
      video.removeAttribute('poster');
      video.removeAttribute('autoplay');
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      video.addEventListener('playing', () => {
        this.isPlaying = true;
        this.classList.add('is-playing');
        this.render();
      });
      video.addEventListener('pause', () => {
        this.isPlaying = false;
        this.render();
      });
      // iOS can leave the first play() pending while it buffers: ask again once enough has loaded.
      video.addEventListener('canplay', () => {
        if (this.wantsPlay && this.inView && video.paused) this.sync();
      });
      this.template.after(video);
      this.video = video;
      return video;
    }

    sync() {
      if (!this.video) {
        if (this.wantsPlay && this.inView) this.attach();
        if (!this.video) return this.render();
      }
      if (this.wantsPlay && this.inView) {
        const attempt = this.video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch((error) => {
            // Only a refusal (NotAllowedError: iOS Low Power Mode, data saver) stops autoplay: show Play and let the
            // visitor start it. Anything else, like the AbortError a first play() gets when the stream starts loading,
            // is retried on `canplay`.
            if (error && error.name === 'NotAllowedError') {
              this.wantsPlay = false;
              this.render();
            }
          });
        }
      } else if (!this.video.paused) {
        this.video.pause();
      }
      this.render();
    }

    togglePlay() {
      // The button shows what the film is doing, so a press means the opposite of that.
      this.wantsPlay = !this.isPlaying;
      if (this.wantsPlay) {
        this.attach();
        // A press means "play now", even when the observer has not reported the section as in view yet.
        this.inView = true;
      }
      this.sync();
    }

    render() {
      const playing = this.isPlaying;
      this.dataset.playing = String(playing);
      if (this.playButton) {
        this.playButton.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
      }
    }
  }

  customElements.define('st-home-film', StHomeFilm);
})();
